import React from 'react'
import PropTypes from 'prop-types'
import { TextField } from '@material-ui/core'
import { Field } from 'react-formality'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import Img from './components/Img'
import OwnWant from './components/OwnWant'
import UpDownVote from './components/UpDownVote'
import voteTypes from './enums/voteTypes'
import AddButton from './components/AddButton'
import ClampedText from './components/ClampedText'
import Tags from './components/Tags'
import UserBar from './components/UserBar'
import ImproveBox from './components/ImproveBox'
import HeaderStatistic from './components/HeaderStatistic'
import Collection from './components/Collection'
import ExpandableSection from './components/ExpandableSection'
import Form from './components/Form'
import getNormalisedParameters from './utilities/getNormalisedParameters'
import getField from './utilities/Data/getField'
import connectPropTypes from './utilities/connectPropTypes'
import Thing from './utilities/Data/Thing'
import Post from './utilities/Data/Post'
import KitSchema from './utilities/Data/Kit'
import displayModes from './enums/displayModes'
import entityTypes from './enums/entityTypes'
import errorTypes from './enums/errorTypes'
import messageTypes from './enums/messageTypes'
import styles from './Kit.icss'
import getDecodedURIComponent from './utilities/getDecodedURIComponent'

const voteScore = voteObj => (voteObj.UP || 0) - (voteObj.DOWN || 0)

class Kit extends React.Component {
	constructor(props) {
		super(props)

		this.itemsElement = React.createRef()
		this.postsElement = React.createRef()
		this.suggestionsElement = React.createRef()
		this.scrollTimer = null
		this.form = React.createRef()

		this.clickItemBinder = this.clickItemBinder.bind(this)
		this.clickItemCount = this.clickItemCount.bind(this)
		this.clickPostCount = this.clickPostCount.bind(this)
		this.clickHeaderBinder = this.clickHeaderBinder.bind(this)
		this.handleValidFormSubmit = this.handleValidFormSubmit.bind(this)
		this.handleOwnSet = this.handleOwnSet.bind(this)
		this.handleWantSet = this.handleWantSet.bind(this)
		this.handleDialogDelete = this.handleDialogDelete.bind(this)
		this.handleDialogCancel = this.handleDialogCancel.bind(this)

		this.state = { dialogShown: false }
	}

	componentDidMount() {
		const parsedSearch = getNormalisedParameters(this.props.location.search)
		const kitId = getDecodedURIComponent(parsedSearch.kitid || this.props.match.params.kitId)
		if (kitId) {
			this.props.kitFetch({ kitId })
			this.setState({ kitId })
		} else {
			this.props.appMessage(messageTypes.ERROR, this.context.translate('Kit id not specificed'), errorTypes.KEYNOTSPECIFIED)
		}
	}

	componentDidUpdate(prevProps) {
		if (prevProps.kit !== this.props.kit) {
			this.props.appAddContextMenu({
				func: () => {
					this.props.kitDuplicate({ kitId: this.state.kitId })
				},
				text: this.context.translate('Duplicate kit')
			})
			this.props.appAddContextMenu({
				func: () => {
					this.handleOwnSet(!this.props.kit.own)
				},
				text: this.context.translate('Own kit')
			})
			this.props.appAddContextMenu({
				func: () => {
					this.props.appRedirect(`/post/edit?kitid=${this.props.kit.kitId}`)
				},
				text: this.context.translate('Post kit')
			})
			//	For your eyes only
			if (this.props.kit && this.props.user.uid === this.props.kit.uid) {
				this.props.appShowBottomBar(false)
				this.props.appShowBottomNavBar(true)
				this.props.appBottomNavBarLabelNext(this.context.translate('DONE'))
				this.props.appBottomNavBarHandleClick(() => {
					this.form.current.submit()
					this.props.appRedirect(`/user/${getField(this.props.user, 'userProfile.userName')}`)
				})
				this.props.appAddContextMenu({
					func: () => {
						this.setState({ dialogShown: true })
					},
					text: this.context.translate('Delete kit')
				})
				this.props.appAddContextMenu({
					func: () => {
						this.props.appRedirect(`/kit/edit/${this.props.kit.kitId}`)
					},
					text: this.context.translate('Edit kit')
				})
				this.props.kitDraftCreate({ kit: this.props.kit })
			}
			if (this.props.kit && this.state.kitId) {
				if (this.state.kitId !== this.props.kit.kitId) {
					//	If we duplicated a kit.
					this.props.appRedirect(`/kit/edit/${this.props.kit.kitId}`)
				}
			}
		}
	}

	handleValidFormSubmit({ values }) {
		this.props.kitDraftSet({ kitDraft: { ...this.props.kitDraft, ...values } })
		this.props.appMessage(messageTypes.POSITIVE, this.context.translate('Saved to your kits'))
	}

	handleOwnSet(own) {
		this.props.kitOwnSet({ own, kit: this.props.kit })
	}

	handleWantSet(want) {
		this.props.kitWantSet({ want, kit: this.props.kit })
	}

	handleDialogCancel() {
		this.setState({ dialogShown: false })
	}

	handleDialogDelete() {
		this.setState({ dialogShown: false })
		this.props.kitDelete({ kitId: this.state.kitId })
		this.props.appRedirect('/')
	}

	clickItemBinder(itemKey, colourName) {
		const me = this
		return (e) => {
			e.preventDefault()
			me.props.appRedirect(`/item/${itemKey}?colour=${encodeURIComponent(colourName)}`)
		}
	}

	clickHeaderBinder(ref) {
		return () => {
			clearTimeout(this.scrollTimer)
			this.scrollTimer = setTimeout(() => {
				ref.current.scrollIntoView({ block: 'start', behavior: 'smooth' })
			}, 0)
		}
	}

	clickItemCount() {
		clearTimeout(this.scrollTimer)
		this.scrollTimer = setTimeout(() => {
			this.itemsElement.current.scrollIntoView({ block: 'start', behavior: 'smooth' })
		}, 0)
	}

	clickPostCount() {
		clearTimeout(this.scrollTimer)
		this.scrollTimer = setTimeout(() => {
			this.postsElement.current.scrollIntoView({ block: 'start', behavior: 'smooth' })
		}, 0)
	}

	render() {
		let { kit, kitDraft, user, kitPosts, kitOwnById, kitWantById, itemOwnByKey, itemWantByKey, kitSuggestions } = this.props // eslint-disable-line prefer-const

		if (!kit) {
			return null
		}

		const numKitItems = (kit.items && kit.items.length) || 0
		const imageContent = (kit.images && kit.images.length !== 0 && kit.images) || kit.imageUrl
		const kitItems = kit.items && kit.items.filter(kitItem => kitItem.item)
		const numSuggestions = (kitSuggestions && kitSuggestions.length) || 0

		const renderAfterSuggestedItem = (renderProps) => {
			const { item } = renderProps
			return (
				<section className={styles.afterItem}>
					<div className={styles.voteBox}>
						<div className={styles.voteBoxLine}>
							<span className={styles.featureIcons}>
								<UpDownVote
									vote={voteTypes[item.userVoteType ? item.userVoteType : 'NEUTRAL']}
									onVoteChange={(result) => {
										const kitSuggestion = {
											userName: this.props.user.userProfile.userName,
											kitId: this.state.kitId,
											colourName: item.colourName,
											itemKey: item.itemKey,
											voteType: result.type
										}

										this.props.kitSuggestionSet({ kitSuggestion })
									}}
									score={voteScore(item.vote)}
								/>
							</span>
						</div>
						<div className={styles.voteBoxLine}>
							{this.context.translate('{by}{user}', {
								by: {
									value: this.context.translate('by'),
									display: value => <span>{value}&nbsp;</span>
								},
								user: {
									value: item.userName || '',
									display: value => <span className={styles.userName}>@{value}</span>
								}
							})}
						</div>
					</div>
					{kit.uid === user.uid && (
						<div className={styles.buttonBox}>
							<Button
								variant="contained"
								classes={{ root: styles.addButton }}
								onClick={() => this.props.kitSuggestionAddToDraft({ item })}
							>
								{this.context.translate('Add')}
							</Button>
						</div>
					)}
				</section>
			)
		}

		const collectionProps = Post.feedCollectionProps

		return (
			<section className={styles.pageSection}>
				<div className={styles.kitPageContainer}>
					<UserBar userName={kit.userName} profileImages={kit.profileImages}>
						<>
							<HeaderStatistic count={kit.countPosts} name={this.context.translate('POSTS')} onClick={this.clickHeaderBinder(this.postsElement)} />
							<HeaderStatistic count={numKitItems} name={this.context.translate('ITEMS')} onClick={this.clickHeaderBinder(this.itemsElement)} />
							<HeaderStatistic count={kit.countWanted} name={this.context.translate('WANTS')} />
						</>
					</UserBar>

					<section className={styles.leadImage}>
						<Tags
							readOnly
							hideTagsInitially
							tags={kit.items && kit.items.length !== 0 ? kit.tags : undefined}
							clickTag={(tag) => {
								this.props.appRedirect(Thing.href(tag))
							}}
						>
							<>
								{imageContent && (
									<Img
										content={imageContent}
										style={{ width: '100%' }}
									/>
								)}
							</>
						</Tags>
					</section>

					<section className={styles.ownWantContainer}>
						<OwnWant
							showOwn={false}
							own={KitSchema.isOwned(kitOwnById)(kit)}
							want={KitSchema.isWanted(kitWantById)(kit)}
							countOwned={kit.countOwned}
							countWanted={kit.countWanted}
							ownSet={this.handleOwnSet}
							wantSet={this.handleWantSet}
							showFits
							//	TODO: remove title, as it is only used for fake KF score
							title={kit.kitId}
						/>
					</section>

					<section className={styles.section}>
						<div ref={this.postsElement} className={styles.refHead} />
						{kitPosts && kitPosts.length > 0 && (
						<>
							<ExpandableSection title={kitPosts ? this.context.translate('{posts|# - Activity post|# - Activity posts}', { posts: kit.countPosts || 0 }) : this.context.translate('Activity Posts')} defaultExpanded>
								<Collection
									items={kitPosts}
									{...collectionProps}
									itemShowOwnWant={false}
									allowedDisplayModes={[displayModes.LIST.type, displayModes.GRID.type]}
									defaultDisplayMode={displayModes.GRID.type}
									itemOnSelect={post => this.props.appRedirect(collectionProps.itemHref(post))}
								/>
							</ExpandableSection>
							<div className={styles.buttonLine}>
								<AddButton onClick={() => this.props.appRedirect(`/post/edit?kitid=${kit.kitId}`)}>
									{this.context.translate('Add activity post')}
								</AddButton>
							</div>
						</>
						)}
						{!(kitPosts && kitPosts.length > 0) && (
							<ImproveBox className={styles.customImproveBox} text={this.context.translate('Post an example of this kit in use!')}>
								<div className={styles.buttonLine}>
									<AddButton onClick={() => this.props.appRedirect(`/post/edit?kitid=${kit.kitId}`)}>
										{this.context.translate('Add activity post')}
									</AddButton>
								</div>
							</ImproveBox>
						)}
					</section>

					{user.uid !== kit.uid && (
						<section className={styles.description}>
							<ClampedText>
								{kit.description}
							</ClampedText>
						</section>
					)}
					{kitDraft && user.uid === kit.uid && (
						<Form
							initialValues={{
								name: kitDraft.name,
								caption: kitDraft.caption
							}}
							onSubmit={args => (args.isValid && this.handleValidFormSubmit(args))}
							ref={this.form}
						>
							{props => (
								<React.Fragment>
									<section className={styles.pagePadding}>
										<Field
											component={TextField}
											name="name"
											value={props.values.name}
											fullWidth
											margin="normal"
											label={this.context.translate('Name')}
										/>
									</section>
									<section className={styles.pagePadding}>
										<Field
											component={TextField}
											name="caption"
											value={props.values.caption}
											fullWidth
											multiline
											margin="normal"
											label={this.context.translate('Caption')}
										/>
									</section>
								</React.Fragment>
							)}
						</Form>
					)}

					<section className={styles.section}>
						<div ref={this.itemsElement} className={styles.refHead} />
						<ExpandableSection title={this.context.translate('{numItems|1 item in this kit|# items in this kit}', { numItems: numKitItems })} defaultExpanded>
							{kitItems && (
								<Collection
									classes={{ itemRoot: styles.itemRoot }}
									items={kitItems}
									itemTitle={kitItem => Thing.title(kitItem.item)}
									itemSubtitle={kitItem => Thing.subtitle(kitItem.item)}
									itemImageContent={kitItem => kitItem.itemImages}
									itemHref={kitItem => Thing.href(kitItem.item)}
									itemOnSelect={kitItem => this.props.appRedirect(Thing.href({ ...kitItem.item, colour: { colourName: kitItem.colourName } }))}
									itemShowOwnWant
									itemOwn={kitItem => Thing.isOwned({ itemOwnByKey })(kitItem.item, kitItem.colourName)}
									itemWant={kitItem => Thing.isWanted({ itemWantByKey })(kitItem.item, kitItem.colourName)}
									itemOwnSet={Thing.propertyGetter({
										[entityTypes.ITEM.type]: kitItem => own => this.props.itemOwnSet({ own, item: kitItem })
									})}
									itemWantSet={Thing.propertyGetter({
										[entityTypes.ITEM.type]: kitItem => want => this.props.itemWantSet({ want, item: kitItem })
									})}
								/>
							)}
						</ExpandableSection>
						{user.uid === kit.uid && (
							<div className={styles.buttonLine}>
								<AddButton onClick={() => this.props.appRedirect(`/kit/edit/${kit.kitId}`)}>
									{this.context.translate('Edit items')}
								</AddButton>
							</div>
						)}
					</section>

					<section className={styles.section}>
						<div ref={this.suggestionsElement} className={styles.refHead} />
						{kitSuggestions && (
							<ExpandableSection title={this.context.translate('{numItems|1 item suggested for this kit|# items suggested for this kit}', { numItems: numSuggestions })} defaultExpanded>
								<Collection
									classes={{ itemRoot: styles.itemRoot }}
									items={kitSuggestions}
									itemTitle={kitItem => Thing.title(kitItem.item)}
									itemSubtitle={kitItem => Thing.subtitle(kitItem.item)}
									itemImageContent={kitItem => kitItem.itemImages}
									itemHref={kitItem => Thing.href(kitItem.item)}
									renderAfterItem={renderAfterSuggestedItem}
								/>
							</ExpandableSection>
						)}
						<div className={styles.buttonLine}>
							<AddButton onClick={() => {
								this.props.searchParametersClear()
								this.props.appRedirect(`/kit/suggest/${kit.kitId}`)
							}}
							>
								{this.context.translate('Add your suggestion')}
							</AddButton>
						</div>
					</section>
				</div>
				<Dialog
					open={this.state.dialogShown}
					onClose={this.handleDialogClose}
					aria-labelledby="alert-dialog-title"
					aria-describedby="alert-dialog-description"
				>
					<DialogTitle id="alert-dialog-title">{this.context.translate('Delete kit?')}</DialogTitle>
					<DialogContent>
						<DialogContentText id="alert-dialog-description">
							{this.context.translate('Are you sure you want to delete this kit?')}
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={this.handleDialogDelete} color="primary">
							{this.context.translate('Yes')}
						</Button>
						<Button onClick={this.handleDialogCancel} color="primary" autoFocus>
							{this.context.translate('No')}
						</Button>
					</DialogActions>
				</Dialog>
			</section>
		)
	}
}

Kit.propTypes = {
	location: PropTypes.shape({
		search: PropTypes.string
	}).isRequired,
	match: PropTypes.shape({
		params: PropTypes.object.isRequired
	}).isRequired,
	kitFetch: PropTypes.func.isRequired,
	kitDraftCreate: PropTypes.func.isRequired,
	appMessage: PropTypes.func.isRequired,
	appAddContextMenu: PropTypes.func.isRequired,
	appRedirect: PropTypes.func.isRequired,
	appShowBottomBar: PropTypes.func.isRequired,
	appShowBottomNavBar: PropTypes.func.isRequired,
	appBottomNavBarLabelNext: PropTypes.func.isRequired,
	appBottomNavBarHandleClick: PropTypes.func.isRequired,
	searchParametersClear: PropTypes.func.isRequired,
	kitSuggestionSet: PropTypes.func.isRequired,
	itemOwnSet: PropTypes.func.isRequired,
	itemWantSet: PropTypes.func.isRequired,
	kitOwnById: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	kitWantById: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	kitOwnSet: PropTypes.func.isRequired,
	kitWantSet: PropTypes.func.isRequired,
	kitDuplicate: PropTypes.func.isRequired,
	kitDelete: PropTypes.func.isRequired,
	kitSuggestionAddToDraft: PropTypes.func.isRequired,
	kitDraftSet: PropTypes.func.isRequired,
	kit: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	kitDraft: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	kitPosts: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	itemOwnByKey: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	itemWantByKey: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	kitSuggestions: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	user: PropTypes.object // eslint-disable-line react/forbid-prop-types
}

Kit.defaultProps = {
	kit: undefined,
	kitDraft: undefined,
	kitPosts: undefined,
	user: undefined,
	kitOwnById: undefined,
	kitWantById: undefined,
	itemOwnByKey: undefined,
	itemWantByKey: undefined,
	kitSuggestions: undefined
}

Kit.contextTypes = {
	translate: PropTypes.func
}

export const testing = {
	Kit
}

export default connectPropTypes(Kit)



// WEBPACK FOOTER //
// ./src/Kit.jsx